import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

/**
 * Mevcut kullanıcının görebileceği çalışan kimliklerini hesaplar.
 * Operasyon Yönetimi ve SMO Lead Raporları ile aynı kurallar:
 *
 *  - admin (isAdmin=true) → kısıtlama yok (null döner)
 *  - SMO Lead            → merkez sahipliği (Centers.SMO === employeeId) üzerinden,
 *                          o merkezlere atanmış tüm çalışanlar
 *  - Yedek SMO (merkez)  → Merkez Yönetimi'nde Centers.SMOBackup === employeeId olan
 *                          merkezlerin çalışanları (primer SMO ile aynı görünüm)
 *  - Backup SMO Lead     → Proje Yönetimi'nde bir projeye role "SMO Lead",
 *    (proje)               workerType "backup" olarak eklenen kullanıcı; o projede
 *                          primer SMO Lead'in merkezlerine atanmış çalışanlar
 *  - Bölge Yöneticisi    → Regions koleksiyonunda managerId/cityManagerIds eşleşmesi;
 *                          bölge illerindeki merkezlerin çalışanları
 *  - diğer herkes        → yalnızca kendisi
 *
 * Dönüş: null = kısıtlama yok; Set = izin verilen çalışan id'leri.
 * Liste çözülene kadar non-admin kullanıcılar için boş Set (güvenli taraf) döner.
 */
const useAllowedEmployeeIds = (isAdmin, employeeId) => {
  // null = kısıtlama yok (admin)
  const [allowedEmployeeIds, setAllowedEmployeeIds] = useState(null);

  useEffect(() => {
    // Admins see everyone — no restriction.
    if (isAdmin) {
      setAllowedEmployeeIds(null);
      return;
    }
    // Until we know who the user is, fail closed.
    if (!employeeId) {
      setAllowedEmployeeIds(new Set());
      return;
    }

    // Fail closed while the allowed list resolves.
    setAllowedEmployeeIds(new Set());

    const fetchAllowedEmployees = async () => {
      try {
        const [centersSnapshot, projectsSnapshot, regionsSnapshot] = await Promise.all([
          getDocs(collection(db, 'Centers')),
          getDocs(collection(db, 'Projects')),
          getDocs(collection(db, 'Regions')),
        ]);

        // centerId -> smoId, from the Centers collection
        const centerToSmoMap = {};
        // Centers where the current user is the "Yedek SMO" (backup). They
        // mirror the primer SMO's view for those centers, as in SMO Lead Reports.
        const myBackupCenterIds = new Set();
        centersSnapshot.docs.forEach((centerDoc) => {
          const { SMO: smoId, SMOBackup: backupId } = centerDoc.data();
          if (smoId) centerToSmoMap[centerDoc.id] = smoId;
          if (backupId && backupId === employeeId) myBackupCenterIds.add(centerDoc.id);
        });

        // Resolve each center's owning SMO, recovering deleted centers (not in
        // the Centers collection) the same way SMO Lead Reports does:
        //   1. an employee in that center carries an `smoId` field, or
        //   2. another center in the same project is owned by a known SMO.
        const centerOwner = {}; // centerId -> resolved smoId
        const processedCenters = new Set();
        projectsSnapshot.docs.forEach((projectDoc) => {
          const projectEmployees = projectDoc.data().employees || [];
          const centerEmployeesMap = {};
          projectEmployees.forEach((emp) => {
            if (!emp.centerId) return;
            if (!centerEmployeesMap[emp.centerId]) centerEmployeesMap[emp.centerId] = [];
            centerEmployeesMap[emp.centerId].push(emp);
          });

          for (const [centerId, centerEmployees] of Object.entries(centerEmployeesMap)) {
            if (processedCenters.has(centerId)) continue;
            let smoId = centerToSmoMap[centerId];
            if (!smoId) {
              const empWithSmo = centerEmployees.find((e) => e.smoId);
              if (empWithSmo) smoId = empWithSmo.smoId;
              if (!smoId) {
                for (const otherId of Object.keys(centerEmployeesMap)) {
                  if (otherId !== centerId && centerToSmoMap[otherId]) {
                    smoId = centerToSmoMap[otherId];
                    break;
                  }
                }
              }
              if (!smoId) continue;
            }
            centerOwner[centerId] = smoId;
            processedCenters.add(centerId);
          }
        });

        // Check if user is Region Manager
        const normalizeCity = (city) => city.toLowerCase().trim()
          .replace(/ı/g, 'i')
          .replace(/ğ/g, 'g')
          .replace(/ü/g, 'u')
          .replace(/ş/g, 's')
          .replace(/ö/g, 'o')
          .replace(/ç/g, 'c');

        let isRegionManager = false;
        const myRegionCenterIds = new Set();

        regionsSnapshot.docs.forEach((regionDoc) => {
          const regionData = regionDoc.data();
          const managerId = regionData.managerId;
          const cityManagerIds = regionData.cityManagerIds || {};

          // Check if user is the main region manager or a city manager
          if (managerId === employeeId || Object.values(cityManagerIds).includes(employeeId)) {
            isRegionManager = true;

            // Get all cities in this region
            const cities = regionData.cities || [];

            // Find all centers in these cities
            centersSnapshot.docs.forEach((centerDoc) => {
              const centerData = centerDoc.data();
              const centerCity = centerData['İl'] || '';

              if (cities.some(city => normalizeCity(city) === normalizeCity(centerCity))) {
                myRegionCenterIds.add(centerDoc.id);
              }
            });
          }
        });

        // If user is Region Manager, get all employees in their region's centers
        if (isRegionManager && myRegionCenterIds.size > 0) {
          const workerIds = new Set();
          projectsSnapshot.docs.forEach((projectDoc) => {
            const projectEmployees = projectDoc.data().employees || [];
            projectEmployees.forEach((emp) => {
              if (emp.id && emp.centerId && myRegionCenterIds.has(emp.centerId)) {
                workerIds.add(emp.id);
              }
            });
          });
          // The Region Manager themselves is always selectable
          workerIds.add(employeeId);
          setAllowedEmployeeIds(workerIds);
          return;
        }

        // Centers of a given SMO (resolved + direct ownership)
        const centerIdsOwnedBy = (smoId) => {
          const ids = new Set(
            Object.keys(centerOwner).filter((cid) => centerOwner[cid] === smoId)
          );
          Object.keys(centerToSmoMap).forEach((cid) => {
            if (centerToSmoMap[cid] === smoId) ids.add(cid);
          });
          return ids;
        };

        // Centers the user owns as primer SMO, plus the ones they back up.
        const myCenterIds = centerIdsOwnedBy(employeeId);
        myBackupCenterIds.forEach((cid) => myCenterIds.add(cid));

        const workerIds = new Set();

        // Projects where the user is the "backup" SMO Lead (Proje Yönetimi →
        // Çalışanlar, role "SMO Lead" + workerType "backup"). Same rule as the
        // delegated views in SMO Lead Reports: within that project, they see
        // the workers assigned to the primer SMO Lead's centers.
        const isSmoLead = (emp, type) =>
          String(emp.role || '').toLowerCase() === 'smo lead' &&
          String(emp.workerType || '').toLowerCase() === type;

        projectsSnapshot.docs.forEach((projectDoc) => {
          const projectEmployees = projectDoc.data().employees || [];
          const isBackupHere = projectEmployees.some(
            (emp) => emp.id === employeeId && isSmoLead(emp, 'backup')
          );
          if (!isBackupHere) return;

          const primerCenterIds = new Set();
          projectEmployees
            .filter((emp) => emp.id && emp.id !== employeeId && isSmoLead(emp, 'primer'))
            .forEach((primer) => {
              centerIdsOwnedBy(primer.id).forEach((cid) => primerCenterIds.add(cid));
            });

          projectEmployees.forEach((emp) => {
            if (emp.id && emp.centerId && primerCenterIds.has(emp.centerId)) {
              workerIds.add(emp.id);
            }
          });
        });

        // Neither an SMO, a Yedek SMO, nor a backup SMO Lead → only themselves.
        if (myCenterIds.size === 0 && workerIds.size === 0) {
          setAllowedEmployeeIds(new Set([employeeId]));
          return;
        }

        // SMO / Yedek SMO: employees assigned to those centers across all projects
        projectsSnapshot.docs.forEach((projectDoc) => {
          const projectEmployees = projectDoc.data().employees || [];
          projectEmployees.forEach((emp) => {
            if (emp.id && emp.centerId && myCenterIds.has(emp.centerId)) {
              workerIds.add(emp.id);
            }
          });
        });
        // The SMO themselves is always selectable.
        workerIds.add(employeeId);

        setAllowedEmployeeIds(workerIds);
      } catch (error) {
        console.error('Error computing allowed employees:', error);
        // Fail closed: restrict to self rather than exposing all employees
        setAllowedEmployeeIds(new Set([employeeId]));
      }
    };

    fetchAllowedEmployees();
  }, [isAdmin, employeeId]);

  return allowedEmployeeIds;
};

export default useAllowedEmployeeIds;
